import * as React from 'react';
import { cn } from '../../lib/utils';
import { kitT, type KitTranslateFn } from '../../lib/kitTranslate';
import { CanvasSizeContext } from './CanvasSizeContext';

/** Parse aspect ratio string (e.g. "16/9" or "1920/1080") to numeric ratio width/height */
function parseAspectRatio(str: string): number {
    const [w, h] = str.split('/').map((s) => parseFloat(s.trim()));
    return Number.isFinite(w) && Number.isFinite(h) && h > 0 ? w / h : 16 / 9;
}

/** Compute object-contain display rect: (left, top, width, height) of the image within container */
function getObjectContainRect(
    containerWidth: number,
    containerHeight: number,
    aspectRatio: number
): { left: number; top: number; width: number; height: number } {
    if (containerWidth <= 0 || containerHeight <= 0) {
        return { left: 0, top: 0, width: 0, height: 0 };
    }
    const containerRatio = containerWidth / containerHeight;
    if (containerRatio > aspectRatio) {
        const width = containerHeight * aspectRatio;
        return { left: (containerWidth - width) / 2, top: 0, width, height: containerHeight };
    }
    const height = containerWidth / aspectRatio;
    return { left: 0, top: (containerHeight - height) / 2, width: containerWidth, height };
}

export interface ImageDropCanvasProps {
    src: string;
    alt?: string;
    children?: React.ReactNode;
    className?: string;
    aspectRatio?: string;
    useImageAspectRatio?: boolean;
    fitViewport?: boolean;
    onImageClick?: (position: { x: number; y: number }) => void;
    translate?: KitTranslateFn;
}

export const ImageDropCanvas: React.FC<ImageDropCanvasProps> = ({
    src,
    alt = '',
    children,
    className,
    aspectRatio = '16/9',
    useImageAspectRatio = true,
    fitViewport = false,
    onImageClick,
    translate,
}) => {
    const [imageAspectRatio, setImageAspectRatio] = React.useState<string | null>(null);
    const effectiveAspectRatio = imageAspectRatio ?? aspectRatio;

    const handleImageLoad = React.useCallback(
        (e: React.SyntheticEvent<HTMLImageElement>) => {
            if (!useImageAspectRatio) return;
            const img = e.currentTarget;
            if (img.naturalWidth > 0 && img.naturalHeight > 0) {
                setImageAspectRatio(`${img.naturalWidth}/${img.naturalHeight}`);
            }
        },
        [useImageAspectRatio]
    );

    React.useEffect(() => {
        if (!useImageAspectRatio || !src?.trim()) return;
        setImageAspectRatio(null);
    }, [src, useImageAspectRatio]);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const [canvasSize, setCanvasSize] = React.useState<{ width: number; height: number } | null>(null);

    React.useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) {
                const { width, height } = entry.contentRect;
                setCanvasSize({ width, height });
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const aspectRatioNum = parseAspectRatio(effectiveAspectRatio);
    const imageRect =
        canvasSize &&
            canvasSize.width > 0 &&
            canvasSize.height > 0
            ? getObjectContainRect(canvasSize.width, canvasSize.height, aspectRatioNum)
            : null;

    const handleClick = React.useCallback(
        (e: React.MouseEvent<HTMLDivElement>) => {
            if (!onImageClick || !containerRef.current || !imageRect) return;
            const rect = containerRef.current.getBoundingClientRect();
            const clipX = e.clientX - rect.left - imageRect.left;
            const clipY = e.clientY - rect.top - imageRect.top;
            const x = imageRect.width > 0 ? (clipX / imageRect.width) * 100 : 0;
            const y = imageRect.height > 0 ? (clipY / imageRect.height) * 100 : 0;
            onImageClick({ x, y });
        },
        [onImageClick, imageRect]
    );

    if (!src?.trim()) {
        return (
            <div
                className={cn(
                    'relative w-full overflow-hidden rounded-lg border-2 border-dashed border-muted-foreground/30 bg-muted flex items-center justify-center text-muted-foreground',
                    className
                )}
                style={{ aspectRatio: effectiveAspectRatio }}
            >
                <span className="text-sm">
                    {kitT(translate, 'kIdcEmpty', 'No image')}
                </span>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={cn(
                'relative w-full overflow-hidden rounded-lg bg-muted',
                fitViewport && 'max-h-[min(50vh,500px)]',
                className
            )}
            style={{ aspectRatio: effectiveAspectRatio }}
        >
            <img
                src={src}
                alt={alt}
                className="absolute inset-0 h-full w-full object-contain"
                onLoad={handleImageLoad}
            />
            <CanvasSizeContext.Provider
                value={
                    imageRect
                        ? {
                            width: imageRect.width,
                            height: imageRect.height,
                            left: imageRect.left,
                            top: imageRect.top,
                        }
                        : null
                }
            >
                <div
                    className={cn(
                        'absolute',
                        !imageRect && 'inset-0',
                        onImageClick && 'cursor-crosshair'
                    )}
                    style={
                        imageRect
                            ? {
                                left: imageRect.left,
                                top: imageRect.top,
                                width: imageRect.width,
                                height: imageRect.height,
                            }
                            : undefined
                    }
                    onClick={onImageClick ? handleClick : undefined}
                    role={onImageClick ? 'button' : undefined}
                    aria-label={
                        onImageClick
                            ? kitT(translate, 'kIdcAriaAddHotspot', 'Click to add hotspot')
                            : undefined
                    }
                >
                    {children}
                </div>
            </CanvasSizeContext.Provider>
        </div>
    );
};

ImageDropCanvas.displayName = 'ImageDropCanvas';
